// This hook allows us to dynamically load external scripts and track their loading status in React.
// It is used to load in the external Strich Javascript Modules Library into React, for things like customizr, cart, img2product, buildmockup, etc.

// Use React so we can keep track of the script state.
import { useEffect, useState } from "react";

// Load a script when needed and tell the page if it is ready.
export function useExternalScript(src) {

    // Start as loading when we have a script URL, or idle when we do not.
    const [status, setStatus] = useState(src ? "loading" : "idle");

    // Run this when the script URL changes.
    useEffect(() => {
    
        // If there is no script URL, there is nothing to load.
        if (!src) {
    
            // Show the idle state when no script URL is given.
            setStatus("idle");
            // There is nothing to clean up here.
            return undefined;
    
        }

        // Try to find the script first so we do not add it twice.
        let script = document.querySelector(`script[src="${src}"]`);

        // Make a new script tag only if we could not find one.
        if (!script) {

            // Create the script tag.
            script = document.createElement("script");
            
            // Set the script URL.
            script.src = src;
            
            // Let the browser load it in the background.
            script.async = true;
            
            // Save the current state on the script tag itself.
            script.setAttribute("data-status", "loading");
            
            // Add the script to the page so it can start loading.
            document.body.appendChild(script);

            // Mark the script as ready when it finishes loading.
            script.onload = () => script.setAttribute("data-status", "ready");
            
            // Mark the script as failed if loading breaks.
            script.onerror = () => script.setAttribute("data-status", "error");
        
        }

        // Copy the script state into React state.
        const setStateFromEvent = () => {

            // Use loading if the saved state is missing for any reason.
            setStatus(script.getAttribute("data-status") || "loading");
        
        };

        // Update state when the script loads.
        script.addEventListener("load", setStateFromEvent);
        
        // Update state when the script fails.
        script.addEventListener("error", setStateFromEvent);

        // Read the current state right away.
        setStateFromEvent();

        // Remove the listeners when this hook is cleaned up.
        return () => {
        
            // Stop listening for load events.
            script.removeEventListener("load", setStateFromEvent);
        
            // Stop listening for error events.
            script.removeEventListener("error", setStateFromEvent);
        
        };
    }, [src]);

    // Give the current script state back to the caller.
    return status;
}